import { AuthMethod, LSL_SESSION_COOKIE, LSL_USER_ID_COOKIE } from 'config/constants';
import useSplitTreatment from 'hooks/useSplitTreatment';
import { loginSuccess } from 'lib/gaTracking/crossPageEvents';
import { useMemberLazyQuery } from 'lib/graphql';
import { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router';
import { ssoInitCompleted, ssoLoginSuccess, ssoLogoutSuccess } from 'store/authentication/sso/actions';
import { SplitIO } from 'types';
import getMemberIdFromLslCookies from 'utils/getMemberIdFromLslCookies';
import jscookie from 'js-cookie';

export const useAuthenticateUser = () => {
  const { isReady, split } = useSplitTreatment(SplitIO.Split.SELF_SERVICE_OAUTH, SplitIO.Treatment.ON);
  const dispatch = useDispatch();
  const { pathname } = useLocation();
  const [authenticateUser, { data, error, loading }] = useMemberLazyQuery({
    fetchPolicy: 'cache-and-network',
  });

  useEffect(() => {
    if (!isReady || !split) {
      return;
    }

    // Start the OAuth authentication process
    // we are using the `hasInitializedSSO` flag to determine if we should show the loading screen or not
    // hasInitializedSSO: true -> done authentication process
    // hasInitializedSSO defaults to false

    const logout = () => {
      // When logout, we need to dispatch the ssoInitCompleted action because of we
      // are using `hasInitializedSSO` as a flag to determine if we should show the loading screen or not
      dispatch(ssoInitCompleted(undefined));
      dispatch(ssoLogoutSuccess());
    };

    // only logout when there is no memberId and we are not on the auth page
    // if there is a memberId, we should use the member id to authenticate the user
    // which it will be handled by the (data && !loading) section
    // if the memberId is not valid, the graphql will return an error
    // which it will be handle by the (error) section
    const memberId = getMemberIdFromLslCookies();
    if (!memberId && !pathname.startsWith('/auth')) {
      logout();
    }

    if (error) {
      jscookie.remove(LSL_USER_ID_COOKIE);
      jscookie.remove(LSL_SESSION_COOKIE);
      logout();
    }

    if (data && !loading) {
      const member = { ...data.member, token: 'OAUTH', isAuthenticated: true };

      // We have to dispatch the ssoInitCompleted action because of
      // we are using `hasInitializedSSO` as a flag to determine if we should show the loading screen or not
      dispatch(ssoInitCompleted(member));
      // We actually don't need to dispatch the ssoLoginSuccess action because initCompleted will set the member
      // however, we are doing it for consistency
      dispatch(ssoLoginSuccess(member));
      loginSuccess(AuthMethod.OAUTH);
    }
  }, [data, dispatch, error, isReady, loading, split, pathname]);

  return authenticateUser;
};
